// Payload normalisation shared by pull, push and merge (WEB_PLAN.md §0, §3.6). Exact ports of
// `SyncReplica.canonical` / `SyncReplica.serverDict` / `SyncReplica.syncDict` (GroundTeam/Sync/SyncReplica.swift:184-225)
// and the `localOnlyKeys` lists (GroundTeam/Sync/ResilientDecoding.swift:77,91).

import type { Collection } from '@/contracts/collections';
import type { Json, JsonObject } from '@/contracts/wire';

/**
 * Keys that are derived or per-device on iOS (visit counters, scores, live GPS position). They never travel: iOS
 * strips them before every upload and ignores them on read, so the web must do the same or a phone would see a
 * spurious change (and an old build's stale value could overwrite a recomputed one).
 */
export const LOCAL_ONLY_KEYS: Readonly<Partial<Record<Collection, readonly string[]>>> = {
  records: ['score', 'scoreReason', 'visitCount', 'lastVisitAt', 'lastVisitResultId', 'lastActivityAt', 'nextScheduledAt', 'updatedAt'],
  users: ['lastLocation', 'lastLocationAt'],
};

/**
 * Fields that are Swift dictionaries with UUID keys. Swift encodes them as a flat `[key, value, …]` array in hash
 * order, which differs between encodes; sorting the pairs makes equal values compare (and upload) equal.
 */
export const UUID_KEYED_FIELDS: readonly string[] = ['customValues'];

/** Day logs are written only by their rep and can be large: iOS keeps no merge base for them (they merge whole). */
export function keepsMergeBase(collection: Collection): boolean {
  return collection !== 'dayLogs';
}

/** A shallow copy of `payload` without the collection's local-only keys (the input is not modified). */
export function stripLocalOnly(collection: Collection, payload: JsonObject): JsonObject {
  const keys = LOCAL_ONLY_KEYS[collection];
  if (!keys || !keys.some((k) => Object.prototype.hasOwnProperty.call(payload, k))) return { ...payload };
  const out: JsonObject = { ...payload };
  for (const key of keys) delete out[key];
  return out;
}

/**
 * `SyncReplica.canonical`: every UUID-keyed field that is a non-empty, even-length flat array whose even slots are
 * all strings gets its pairs sorted by key (plain code-unit order, like Swift `<` on ASCII UUID strings). Anything
 * else (object form, odd length, a non-string key) is left exactly as it is. Returns a shallow copy.
 */
export function canonicalCustomValues(payload: JsonObject): JsonObject {
  let out: JsonObject | null = null;
  for (const field of UUID_KEYED_FIELDS) {
    const flat = payload[field];
    if (!Array.isArray(flat) || flat.length === 0 || flat.length % 2 !== 0) continue;
    const pairs: [string, Json, Json][] = [];
    let valid = true;
    for (let i = 0; i < flat.length; i += 2) {
      const key = flat[i];
      if (typeof key !== 'string') {
        valid = false;
        break;
      }
      pairs.push([key, flat[i], flat[i + 1] as Json]);
    }
    if (!valid) continue;
    pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    out ??= { ...payload };
    out[field] = pairs.flatMap(([, k, v]) => [k, v]);
  }
  return out ?? { ...payload };
}

/**
 * The form every merge input and every outgoing payload takes (`SyncReplica.serverDict` / `syncDict`): local-only
 * keys removed, UUID-keyed fields canonical. Never mutates its input.
 */
export function canonicalPayload(collection: Collection, payload: JsonObject): JsonObject {
  return canonicalCustomValues(stripLocalOnly(collection, payload));
}

/** True when `value` is a plain JSON object (not an array, not null). */
export function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
